import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { sendPlayerState, subscribeToPlayerState } from '../../../../services/gameService';
import { useAppContext } from '../../../../AppContext';
import { Direction, GameState, Difficulty, PowerUpType } from './types.js';
import {
  GRID_SIZE,
  INITIAL_SNAKE_POSITION,
  MIN_SPEED,
  DIFFICULTY_SETTINGS,
  POWER_UP_SPAWN_INTERVAL,
  POWER_UP_DURATION,
} from './constants.js';
import { useInterval } from './hooks/useInterval.js';
import { useSwipe } from './hooks/useSwipe.js';
import MobileControls from './MobileControls.js';
import { useTranslation } from 'react-i18next';

// Helper functions remain the same...
const generateWhisper = (currentSnake, powerUp) => {
  let newWhisperPosition;
  do {
    newWhisperPosition = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE),
    };
  } while (
    (powerUp && newWhisperPosition.x === powerUp.x && newWhisperPosition.y === powerUp.y) ||
    currentSnake.some(segment => segment.x === newWhisperPosition.x && segment.y === newWhisperPosition.y)
  );
  return newWhisperPosition;
};
const generatePowerUp = (currentSnake, currentWhisper) => {
  let newPowerUpPosition;
  const type = Math.random() < 0.5 ? PowerUpType.GHOST_MODE : PowerUpType.SLOW_MOTION;
  do {
    newPowerUpPosition = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE),
    };
  } while (
    (newPowerUpPosition.x === currentWhisper.x && newPowerUpPosition.y === currentWhisper.y) ||
    currentSnake.some(segment => segment.x === newPowerUpPosition.x && segment.y === newPowerUpPosition.y)
  );
  return { ...newPowerUpPosition, type };
};
const getRank = (score, t) => {
    if (score > 50) return t('synapticSerpent.ranks.harmonyWeaver');
    if (score > 25) return t('synapticSerpent.ranks.neuralAce');
    if (score > 10) return t('synapticSerpent.ranks.synapticExplorer');
    return t('synapticSerpent.ranks.noviceNavigator');
}

const Overlay = ({ gameState, score, onRestart, onResume, handleSubmit, isSpectatorMode, submissionStatus, lastSoulsAwarded }) => {
    const { t } = useTranslation();
    if (gameState === GameState.PAUSED && !isSpectatorMode) {
        return (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-10 animate-fadeIn">
                <h2 className="text-4xl font-bold text-fuchsia-300" style={{ textShadow: '0 0 8px #f5d0fe' }}>{t('synapticSerpent.overlay.paused')}</h2>
                <button
                  onClick={onResume}
                  className="mt-8 px-6 py-3 border-2 border-cyan-400 text-cyan-400 rounded-lg text-lg font-semibold hover:bg-cyan-400/20 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-[0_0_15px_rgba(103,232,249,0.5)]"
                >
                  {t('synapticSerpent.overlay.resume')}
                </button>
            </div>
        );
    }
    if (gameState === GameState.GAME_OVER) {
        const rank = getRank(score, t);
        return (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-10 animate-fadeIn p-4">
                <div className="text-center">
                    <h2 className="text-3xl md:text-5xl font-bold text-red-500" style={{ textShadow: '0 0 10px #ef4444, 0 0 20px #ef4444' }}>{t('synapticSerpent.overlay.gameOverTitle')}</h2>
                    <p className="text-base md:text-lg text-gray-400 mt-2">{isSpectatorMode ? t('synapticSerpent.overlay.restartingAI') : t('synapticSerpent.overlay.gameOverSubtitle')}</p>
                </div>

                {submissionStatus === 'idle' && !isSpectatorMode && (
                    <div className="my-6 text-center">
                        <p className="text-lg md:text-xl text-cyan-300">{t('synapticSerpent.overlay.finalScore')}</p>
                        <p className="text-6xl md:text-7xl font-bold text-white" style={{ textShadow: '0 0 15px #67e8f9' }}>{score}</p>
                        <p className="text-xl md:text-2xl text-fuchsia-300 mt-4" style={{ textShadow: '0 0 8px #f5d0fe' }}>{rank}</p>
                    </div>
                )}

                {submissionStatus === 'submitting' && <p className="text-lg md:text-xl text-cyan-300 my-8">{t('synapticSerpent.overlay.submitting')}</p>}
                {submissionStatus === 'error' && <p className="text-lg md:text-xl text-red-400 my-8">{t('synapticSerpent.overlay.submitError')}</p>}
                {submissionStatus === 'submitted' && (
                    <div className="my-6 text-center">
                        <p className="text-lg md:text-xl">{t('synapticSerpent.overlay.finalScoreLabel')}: <span className="font-mono text-yellow-300">{score}</span></p>
                        <p className="text-xl md:text-2xl mt-4">{t('synapticSerpent.overlay.soulsAwarded')}: <span className="font-mono text-purple-300">{lastSoulsAwarded}</span></p>
                        
                        <div className="mt-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-center">
                            <p className="text-sm text-red-200 uppercase tracking-widest font-bold mb-1">Raid Boss Damage</p>
                            <p className="text-3xl font-orbitron text-red-400">+{Math.floor(score * 12)}</p>
                        </div>
                    </div>
                )}

                {!isSpectatorMode && (
                  <div className="flex flex-col items-center space-y-4 w-full max-w-xs">
                      {submissionStatus === 'idle' && <button onClick={handleSubmit} className="w-full px-8 py-4 border-2 border-cyan-400 text-cyan-400 rounded-lg text-xl font-semibold hover:bg-cyan-400/20 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-[0_0_15px_rgba(103,232,249,0.5)]">{t('synapticSerpent.overlay.submitButton')}</button>}
                      {(submissionStatus === 'submitted' || submissionStatus === 'error') && <button onClick={onRestart} className="w-full px-8 py-3 bg-indigo-600 text-white rounded-lg text-lg font-semibold hover:bg-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg">{t('synapticSerpent.overlay.playAgainButton')}</button>}
                  </div>
                )}
            </div>
        );
    }
    return null;
};

const SynapticSerpent = ({ dreamWeaveId, nexusId, souls = 0, onGameSubmit, onGameOver, onClose, isSpectatorMode = false, isRemoteMode = false, syncId, myRole, remoteRole, autoStart = false }) => {
  const { t } = useTranslation();
  const { appFunctions } = useAppContext();
  const [gameState, setGameState] = useState(GameState.IDLE);
  const [difficulty, setDifficulty] = useState(Difficulty.MEDIUM);
  const [snake, setSnake] = useState(INITIAL_SNAKE_POSITION);
  const [whisper, setWhisper] = useState(() => generateWhisper(INITIAL_SNAKE_POSITION, null));
  const [direction, setDirection] = useState(Direction.RIGHT);
  const directionQueue = useRef([]);
  const [speed, setSpeed] = useState(DIFFICULTY_SETTINGS[difficulty].INITIAL_SPEED);
  const [score, setScore] = useState(0);
  const [powerUp, setPowerUp] = useState(null);
  const [activePowerUp, setActivePowerUp] = useState(null);
  const [gameLog, setGameLog] = useState([]);
  const [submissionStatus, setSubmissionStatus] = useState('idle'); // idle, submitting, submitted, error
  const [lastSoulsAwarded, setLastSoulsAwarded] = useState(0);
  const lastSyncTime = useRef(0);

  // --- Sync (Remote Mode) ---
  useEffect(() => {
    if (isRemoteMode && syncId && remoteRole) {
        const unsubscribe = subscribeToPlayerState(syncId, remoteRole, (data) => {
            if (data) {
                setSnake(data.snake || []);
                setWhisper(data.whisper || {x:0, y:0});
                setScore(data.score || 0);
                setPowerUp(data.powerUp || null);
                setActivePowerUp(data.activePowerUp || null);
                setGameState(GameState.PLAYING); // Ensure rendering
            }
        });
        return () => unsubscribe();
    }
  }, [isRemoteMode, syncId, remoteRole]);

  const resetGame = useCallback(() => {
    setSnake(INITIAL_SNAKE_POSITION);
    setWhisper(generateWhisper(INITIAL_SNAKE_POSITION, null));
    setDirection(Direction.RIGHT);
    directionQueue.current = [];
    setSpeed(DIFFICULTY_SETTINGS[difficulty].INITIAL_SPEED);
    setScore(0);
    setPowerUp(null);
    setActivePowerUp(null);
    setGameLog([]);
    setSubmissionStatus('idle');
  }, [difficulty]);

  const startGame = useCallback(() => {
    resetGame();
    setGameState(GameState.PLAYING);
    if (!isSpectatorMode) {
      setGameLog(prev => [...prev, { event: 'game-start', timestamp: Date.now() }]);
    }
  }, [resetGame, isSpectatorMode]);

  useEffect(() => {
    if (isSpectatorMode || isRemoteMode || autoStart) startGame();
    else setGameState(GameState.IDLE);
  }, [isSpectatorMode, isRemoteMode, startGame, autoStart]);

  const handleDirectionChange = useCallback((newDirection) => {
    if (gameState !== GameState.PLAYING || isRemoteMode) return;
    if (!isSpectatorMode) {
      setGameLog(prev => [...prev, { event: 'direction-change', newDirection, timestamp: Date.now() }]);
    }
    const lastDirection = directionQueue.current.length > 0 ? directionQueue.current[directionQueue.current.length - 1] : direction;
    if (newDirection === Direction.UP && lastDirection !== Direction.DOWN) directionQueue.current.push(newDirection);
    if (newDirection === Direction.DOWN && lastDirection !== Direction.UP) directionQueue.current.push(newDirection);
    if (newDirection === Direction.LEFT && lastDirection !== Direction.RIGHT) directionQueue.current.push(newDirection);
    if (newDirection === Direction.RIGHT && lastDirection !== Direction.LEFT) directionQueue.current.push(newDirection);
  }, [direction, isSpectatorMode, gameState]);

  const swipeHandlers = useSwipe({ onSwiped: isSpectatorMode ? () => {} : handleDirectionChange });

  // --- AI Brain: A* Pathfinding ---

  const aStarHeuristic = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);

  const findPathWithAStar = (start, end, snakeBody) => {
      const snakeBodySet = new Set(snakeBody.map(seg => `${seg.x},${seg.y}`));
      const startNode = { ...start, g: 0, h: aStarHeuristic(start, end), f: aStarHeuristic(start, end), parent: null };
      const openSet = [startNode];
      const closedSet = new Set();

      while (openSet.length > 0) {
          openSet.sort((a, b) => a.f - b.f);
          const currentNode = openSet.shift();

          if (currentNode.x === end.x && currentNode.y === end.y) {
              let path = [];
              let temp = currentNode;
              while (temp) {
                  path.push(temp);
                  temp = temp.parent;
              }
              return path.reverse();
          }

          closedSet.add(`${currentNode.x},${currentNode.y}`);

          const neighbors = [
              { x: currentNode.x, y: currentNode.y - 1 },
              { x: currentNode.x, y: currentNode.y + 1 },
              { x: currentNode.x - 1, y: currentNode.y },
              { x: currentNode.x + 1, y: currentNode.y },
          ];

          for (const neighbor of neighbors) {
              const neighborKey = `${neighbor.x},${neighbor.y}`;
              if (
                  neighbor.x < 0 || neighbor.x >= GRID_SIZE ||
                  neighbor.y < 0 || neighbor.y >= GRID_SIZE ||
                  closedSet.has(neighborKey) ||
                  snakeBodySet.has(neighborKey)
              ) {
                  continue;
              }

              const gScore = currentNode.g + 1;
              const existingNeighbor = openSet.find(node => node.x === neighbor.x && node.y === neighbor.y);

              if (!existingNeighbor || gScore < existingNeighbor.g) {
                  const hScore = aStarHeuristic(neighbor, end);
                  if (existingNeighbor) {
                      existingNeighbor.g = gScore;
                      existingNeighbor.f = gScore + hScore;
                      existingNeighbor.parent = currentNode;
                  } else {
                      openSet.push({ ...neighbor, g: gScore, h: hScore, f: gScore + hScore, parent: currentNode });
                  }
              }
          }
      }
      return null; // No path found
  };

  const getSafeFallbackMove = (currentSnake, currentDirection) => {
    const head = currentSnake[0];
    const oppositeDirection = { [Direction.UP]: Direction.DOWN, [Direction.DOWN]: Direction.UP, [Direction.LEFT]: Direction.RIGHT, [Direction.RIGHT]: Direction.LEFT };
    const potentialMoves = Object.values(Direction).filter(d => typeof d === 'number' && d !== oppositeDirection[currentDirection]);
    const snakeBodySet = new Set(currentSnake.map(seg => `${seg.x},${seg.y}`));

    const safeMoves = [];
    for (const move of potentialMoves) {
        const newHead = { ...head };
        switch (move) {
            case Direction.UP: newHead.y -= 1; break;
            case Direction.DOWN: newHead.y += 1; break;
            case Direction.LEFT: newHead.x -= 1; break;
            case Direction.RIGHT: newHead.x += 1; break;
        }
        if (newHead.x >= 0 && newHead.x < GRID_SIZE && newHead.y >= 0 && newHead.y < GRID_SIZE && !snakeBodySet.has(`${newHead.x},${newHead.y}`)) {
            safeMoves.push({ move, space: floodFill(newHead, currentSnake) });
        }
    }

    if (safeMoves.length > 0) {
        // If there are safe moves, pick the one that leads to the most open space.
        safeMoves.sort((a, b) => b.space - a.space);
        return safeMoves[0].move;
    }

    // If there are no safe moves, the snake is trapped.
    // As a last resort, try to find ANY move that doesn't hit a wall immediately.
    // Collision with its own body is likely unavoidable.
    const nonWallMoves = potentialMoves.filter(move => {
        const newHead = { ...head };
        switch (move) {
            case Direction.UP: newHead.y -= 1; break;
            case Direction.DOWN: newHead.y += 1; break;
            case Direction.LEFT: newHead.x -= 1; break;
            case Direction.RIGHT: newHead.x += 1; break;
        }
        return newHead.x >= 0 && newHead.x < GRID_SIZE && newHead.y >= 0 && newHead.y < GRID_SIZE;
    });

    if (nonWallMoves.length > 0) {
        return nonWallMoves[0];
    }
    
    // If completely boxed in (e.g., in a corner), any move is a loss.
    return currentDirection;
  };

  const getMoveFromPath = (head, nextNode) => {
      if (nextNode.x > head.x) return Direction.RIGHT;
      if (nextNode.x < head.x) return Direction.LEFT;
      if (nextNode.y > head.y) return Direction.DOWN;
      if (nextNode.y < head.y) return Direction.UP;
      return undefined;
  };

  useInterval(() => {
      if (gameState !== GameState.PLAYING || !isSpectatorMode || isRemoteMode) return;

      const skill = Math.min(1, (souls || 0) / 25000);
      const beSmart = Math.random() < skill;

      let nextMove;

      if (beSmart) {
          let target = whisper;
          let pathToTarget = findPathWithAStar(snake[0], whisper, snake);

          if (powerUp) {
              const pathToPowerUp = findPathWithAStar(snake[0], powerUp, snake);
              if (pathToPowerUp && (!pathToTarget || pathToPowerUp.length < pathToTarget.length)) {
                  target = powerUp;
                  pathToTarget = pathToPowerUp;
              }
          }

          if (pathToTarget && pathToTarget.length > 1) {
              const nextNode = pathToTarget[1];
              const firstMove = getMoveFromPath(snake[0], nextNode);

              // Corrected and Relaxed Safety Check:
              const futureObstacles = snake.length > 1 ? snake.slice(0, snake.length - 1) : [];
              const availableSpace = floodFill(nextNode, futureObstacles);

              // Only abandon the direct path if it leads to an immediate dead end (space <= 1).
              if (availableSpace > 1) {
                  nextMove = firstMove;
              } else {
                  // The direct A* path is a trap, find a safer direction.
                  nextMove = getSafeFallbackMove(snake, direction);
              }
          } else {
              // No path found by A*, use fallback.
              nextMove = getSafeFallbackMove(snake, direction);
          }
      } else {
          // Low-skill AI uses fallback.
          nextMove = getSafeFallbackMove(snake, direction);
      }

      if (typeof nextMove !== 'undefined') {
          directionQueue.current = []; // Clear any stale, buffered moves
          handleDirectionChange(nextMove);
      }
  }, 150);

  const isTargetAdjacent = (head, target) => {
      return Math.abs(head.x - target.x) <= 1 && Math.abs(head.y - target.y) <= 1;
  }

  const floodFill = (startNode, currentSnake) => {
    const snakeBodySet = new Set(currentSnake.map(seg => `${seg.x},${seg.y}`));
    const visited = new Set();
    const queue = [startNode];
    visited.add(`${startNode.x},${startNode.y}`);
    let count = 0;

    while(queue.length > 0) {
        const node = queue.shift();
        count++;
        const neighbors = [{x: node.x+1, y: node.y}, {x: node.x-1, y: node.y}, {x: node.x, y: node.y+1}, {x: node.x, y: node.y-1}];
        for(const neighbor of neighbors) {
            const key = `${neighbor.x},${neighbor.y}`;
            if(neighbor.x >= 0 && neighbor.x < GRID_SIZE && neighbor.y >= 0 && neighbor.y < GRID_SIZE && !snakeBodySet.has(key) && !visited.has(key)) {
                visited.add(key);
                queue.push(neighbor);
            }
        }
    }
    return count;
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSpectatorMode || isRemoteMode) return;
      if (gameState === GameState.IDLE || gameState === GameState.GAME_OVER) return;
      switch (e.key) {
        case 'w': case 'ArrowUp': handleDirectionChange(Direction.UP); break;
        case 's': case 'ArrowDown': handleDirectionChange(Direction.DOWN); break;
        case 'a': case 'ArrowLeft': handleDirectionChange(Direction.LEFT); break;
        case 'd': case 'ArrowRight': handleDirectionChange(Direction.RIGHT); break;
        case 'p': case 'P': togglePause(); break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, handleDirectionChange, isSpectatorMode]);

  useEffect(() => {
    if (gameState === GameState.GAME_OVER && !isSpectatorMode) {
        setGameLog(prev => [...prev, { event: 'game-over', finalScore: score, timestamp: Date.now() }]);
        if (onGameOver) {
            onGameOver();
        }
    }
  }, [gameState, score, isSpectatorMode, onGameOver]);
  
  const tickSpeed = useMemo(() => {
    if (activePowerUp?.type === PowerUpType.SLOW_MOTION) return speed * 1.5;
    return speed;
  }, [speed, activePowerUp]);

  const gameTick = useCallback(() => {
    if (gameState !== GameState.PLAYING) return;
    
    // --- REMOTE MODE: Skip local updates ---
    if (isRemoteMode) return;

    // --- ACTIVE MODE: Sync State ---
    if (!isSpectatorMode && syncId && myRole) {
        const now = Date.now();
        if (now - lastSyncTime.current > 100) { // Throttle 100ms
            sendPlayerState(syncId, myRole, {
                snake,
                whisper,
                score,
                powerUp,
                activePowerUp
            });
            lastSyncTime.current = now;
        }
    }

    if (activePowerUp) {
      setActivePowerUp(prev => prev && prev.duration - tickSpeed <= 0 ? null : { ...prev, duration: prev.duration - tickSpeed });
    }
    let currentDirection = direction;
    if (directionQueue.current.length > 0) {
      const nextDir = directionQueue.current.shift();
      if (typeof nextDir !== 'undefined') {
        setDirection(nextDir);
        currentDirection = nextDir;
      }
    }
    setSnake(prevSnake => {
      const newSnake = [...prevSnake];
      const head = { ...newSnake[0] };
      switch (currentDirection) {
        case Direction.UP: head.y -= 1; break;
        case Direction.DOWN: head.y += 1; break;
        case Direction.LEFT: head.x -= 1; break;
        case Direction.RIGHT: head.x += 1; break;
      }
      if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
        setGameState(GameState.GAME_OVER);
        return prevSnake;
      }
      newSnake.unshift(head);
      let ateSomething = false;
      if (head.x === whisper.x && head.y === whisper.y) {
        ateSomething = true;
        const newScore = score + 1;
        setScore(newScore);
        setSpeed(prevSpeed => Math.max(MIN_SPEED, prevSpeed * DIFFICULTY_SETTINGS[difficulty].SPEED_INCREMENT_FACTOR));
        if (!isSpectatorMode) setGameLog(prev => [...prev, { event: 'eat-whisper', score: newScore, timestamp: Date.now() }]);
        const newWhisper = generateWhisper(newSnake, powerUp);
        setWhisper(newWhisper);
        if (newScore > 0 && newScore % POWER_UP_SPAWN_INTERVAL === 0 && !powerUp) setPowerUp(generatePowerUp(newSnake, newWhisper));
      } else if (powerUp && head.x === powerUp.x && head.y === powerUp.y) {
        ateSomething = true;
        setActivePowerUp({ type: powerUp.type, duration: POWER_UP_DURATION });
        if (!isSpectatorMode) setGameLog(prev => [...prev, { event: 'eat-powerup', powerUpType: powerUp.type, timestamp: Date.now() }]);
        setPowerUp(null);
        setScore(prevScore => prevScore + 5);
      }

      if (!ateSomething) {
          newSnake.pop();
      }

      // Final collision check against the new state
      if (activePowerUp?.type !== PowerUpType.GHOST_MODE) {
        for (let i = 1; i < newSnake.length; i++) {
          if (newSnake[0].x === newSnake[i].x && newSnake[0].y === newSnake[i].y) {
            setGameState(GameState.GAME_OVER);
            return prevSnake; // Return original state before the bad move
          }
        }
      }

      return newSnake;
    });
  }, [gameState, direction, whisper, difficulty, score, powerUp, activePowerUp, tickSpeed, isSpectatorMode]);



  useInterval(gameTick, gameState === GameState.PLAYING ? tickSpeed : null);
  
  useEffect(() => {
    if (isSpectatorMode && gameState === GameState.GAME_OVER) {
      const timer = setTimeout(startGame, 2000);
      return () => clearTimeout(timer);
    }
  }, [gameState, isSpectatorMode, startGame]);

  const togglePause = () => {
    if (isSpectatorMode) return;
    if (gameState === GameState.PLAYING) setGameState(GameState.PAUSED);
    else if (gameState === GameState.PAUSED) setGameState(GameState.PLAYING);
  }

  const handleSubmit = async () => {
    if (isSpectatorMode || submissionStatus === 'submitting') return;

    setSubmissionStatus('submitting');
    try {
        if (onGameSubmit) {
            const data = await onGameSubmit('synapticSerpent', score, gameLog);
            if (data && data.success) {
                setLastSoulsAwarded(data.soulsAwarded || 0);
            }
        }
        setSubmissionStatus('submitted');
    } catch (err) {
        console.error("Error submitting game score:", err);
        setSubmissionStatus('error');
    }
  };

  const renderCell = (x, y) => {
    const isSnake = snake.some(segment => segment.x === x && segment.y === y);
    const isSnakeHead = snake[0].x === x && snake[0].y === y;
    const isWhisper = whisper.x === x && whisper.y === y;
    const isPowerUp = powerUp?.x === x && powerUp?.y === y;
    let cellClass = 'w-full h-full transition-all duration-300';
    let snakeEffectClass = '';
    if (activePowerUp) {
      if (activePowerUp.type === PowerUpType.GHOST_MODE) snakeEffectClass = ' opacity-60';
      else if (activePowerUp.type === PowerUpType.SLOW_MOTION) snakeEffectClass = ' shadow-[0_0_6px_#60a5fa]';
    }
    if (isSnakeHead) cellClass += ` bg-cyan-300 rounded-md shadow-[0_0_8px_#67e8f9] ${snakeEffectClass}`;
    else if (isSnake) cellClass += ` bg-cyan-500 rounded-sm shadow-[0_0_5px_#06b6d4] opacity-80 ${snakeEffectClass}`;
    else if (isWhisper) cellClass += ' bg-fuchsia-400 rounded-full shadow-[0_0_10px_#f0abfc] animate-pulse';
    else if (isPowerUp) {
      if (powerUp?.type === PowerUpType.GHOST_MODE) cellClass += ' bg-white rounded-full shadow-[0_0_12px_#ffffff] animate-pulse';
      else if (powerUp?.type === PowerUpType.SLOW_MOTION) cellClass += ' bg-blue-400 rounded-md shadow-[0_0_12px_#60a5fa] animate-pulse transform rotate-45';
    }
    return <div className={cellClass}></div>;
  };

  return (
    <div className="flex flex-col items-center w-full p-4">
      <div className="w-full max-w-sm md:max-w-md grid grid-cols-3 gap-2 items-center px-2 py-2 mb-4 bg-black/30 rounded-lg border border-cyan-500/30">
        <div className="text-lg text-cyan-200">{t('synapticSerpent.hud.score')}: <span className="font-bold text-white">{score}</span></div>
        <div className="h-full relative">
          {activePowerUp && (
            <div className="text-xs text-center animate-fadeIn">
                <span className={`font-bold ${activePowerUp.type === PowerUpType.GHOST_MODE ? 'text-indigo-300' : 'text-blue-300'}`}>
                    {activePowerUp.type === PowerUpType.GHOST_MODE ? t('synapticSerpent.powerUps.ghostPhase') : t('synapticSerpent.powerUps.chronoShard')}
                </span>
                <div className="w-full bg-gray-700 rounded-full h-1 mt-1">
                    <div className={`h-1 rounded-full transition-all duration-100 ease-linear ${activePowerUp.type === PowerUpType.GHOST_MODE ? 'bg-indigo-400' : 'bg-blue-400'}`} style={{ width: `${(activePowerUp.duration / POWER_UP_DURATION) * 100}%` }}></div>
                </div>
            </div>
          )}
        </div>
        <button onClick={togglePause} disabled={(gameState !== GameState.PLAYING && gameState !== GameState.PAUSED) || isSpectatorMode} className="px-4 py-1 border border-indigo-400 text-indigo-300 rounded-md text-sm hover:bg-indigo-400/20 disabled:opacity-50 disabled:cursor-not-allowed justify-self-end">
          {gameState === GameState.PAUSED ? t('synapticSerpent.hud.resume') : t('synapticSerpent.hud.pause')}
        </button>
      </div>
      <div {...swipeHandlers} className="relative w-full max-w-xs sm:max-w-sm md:max-w-md aspect-square bg-black/50 border-2 border-cyan-500/50 p-1 shadow-[0_0_20px_rgba(0,255,255,0.3)] touch-none">
        {gameState === GameState.IDLE && !isSpectatorMode && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-10 animate-fadeIn">
            <h2 className="text-4xl font-bold text-fuchsia-300" style={{ textShadow: '0 0 8px #f5d0fe' }}>{t('synapticSerpent.idle.title')}</h2>
            <p className="mt-4 mb-6 text-cyan-200 text-center px-4">{t('synapticSerpent.idle.subtitle')}</p>
            <div className="flex space-x-4 mb-8">
                <button onClick={() => setDifficulty(Difficulty.EASY)} className={`px-5 py-2 border-2 rounded-lg font-semibold transition-all duration-300 ${difficulty === Difficulty.EASY ? 'bg-green-400/30 border-green-300 text-white shadow-[0_0_10px_rgba(74,222,128,0.7)]' : 'border-gray-500 text-gray-400 hover:border-green-400 hover:text-green-400'}`} aria-pressed={difficulty === Difficulty.EASY}>{t('synapticSerpent.idle.easy')}</button>
                <button onClick={() => setDifficulty(Difficulty.MEDIUM)} className={`px-5 py-2 border-2 rounded-lg font-semibold transition-all duration-300 ${difficulty === Difficulty.MEDIUM ? 'bg-cyan-400/30 border-cyan-300 text-white shadow-[0_0_10px_rgba(103,232,249,0.7)]' : 'border-gray-500 text-gray-400 hover:border-cyan-400 hover:text-cyan-400'}`} aria-pressed={difficulty === Difficulty.MEDIUM}>{t('synapticSerpent.idle.medium')}</button>
                <button onClick={() => setDifficulty(Difficulty.HARD)} className={`px-5 py-2 border-2 rounded-lg font-semibold transition-all duration-300 ${difficulty === Difficulty.HARD ? 'bg-red-500/30 border-red-400 text-white shadow-[0_0_10px_rgba(239,68,68,0.7)]' : 'border-gray-500 text-gray-400 hover:border-red-500 hover:text-red-500'}`} aria-pressed={difficulty === Difficulty.HARD}>{t('synapticSerpent.idle.hard')}</button>
            </div>
            <button onClick={startGame} className="mt-2 px-8 py-3 border-2 border-cyan-400 text-cyan-400 rounded-lg text-xl font-semibold hover:bg-cyan-400/20 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-[0_0_15px_rgba(103,232,249,0.5)]">{t('synapticSerpent.idle.begin')}</button>
          </div>
        )}
        {(gameState === GameState.PAUSED || gameState === GameState.GAME_OVER) && (
            <Overlay gameState={gameState} score={score} onRestart={startGame} onResume={togglePause} handleSubmit={handleSubmit} isSpectatorMode={isSpectatorMode} submissionStatus={submissionStatus} lastSoulsAwarded={lastSoulsAwarded}/>
        )}
        <div className="grid grid-cols-20 w-full h-full" style={{gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`}}>
          {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, i) => (
            <div key={i} className="bg-transparent aspect-square">
                {renderCell(i % GRID_SIZE, Math.floor(i / GRID_SIZE))}
            </div>
          ))}
        </div>
      </div>
      {!isSpectatorMode && !isRemoteMode && gameState === GameState.PLAYING && <MobileControls onDirectionChange={handleDirectionChange} />}
    </div>
  );
};

export default SynapticSerpent;
